/**
 * §7.5 Everything Included — 5 category groups with module chip lists.
 */

import React from 'react'
import {
  Bot, LineChart, Briefcase, Building2, GraduationCap,
  MessageSquare, BookOpen, Wand2, Zap, BarChart3, FlaskConical, ScanLine,
  Wallet, UsersRound, CreditCard, PieChart, BellRing,
  Palette, Globe, ServerCog, Cog, TrendingUp, BadgeCheck, Sparkles,
} from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { INCLUDED } from '../data'
import { TONE } from '../_shared'

const CAT_ICONS = { Bot, LineChart, Briefcase, Building2, GraduationCap }
const MOD_ICONS = {
  MessageSquare, BookOpen, Wand2, Zap, BarChart3, FlaskConical, ScanLine,
  Wallet, UsersRound, CreditCard, PieChart, BellRing,
  Palette, Globe, ServerCog, Cog, TrendingUp, BadgeCheck,
}

export default function Included() {
  return (
    <section className="relative py-24 md:py-28">
      <Container size="wide" className="flex flex-col gap-10">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>Everything Included</Eyebrow>
          <SectionHeading align="center"
            title={<>Every licence unlocks{' '}<span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">the complete ecosystem.</span></>}
            subtitle="Five module categories. Twenty capabilities. Zero tier gating — the whole platform ships with every subscription."
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {INCLUDED.map((cat) => {
            const tone = TONE[cat.tone] || TONE.orange
            const CatIcon = CAT_ICONS[cat.iconKey] || Sparkles
            return (
              <div key={cat.id} className="relative rounded-2xl border border-white/10 bg-white/[0.02] p-5 md:p-6 backdrop-blur-xl overflow-hidden">
                <div className="pointer-events-none absolute inset-0 opacity-40"
                  style={{ background: `radial-gradient(80% 40% at 20% 0%, rgba(${tone.rgb},0.12), transparent 70%)` }} />

                <div className="relative flex items-center gap-3 pb-3 mb-3 border-b border-white/5">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl border"
                    style={{ borderColor: `rgba(${tone.rgb},0.35)`, background: `rgba(${tone.rgb},0.08)`, color: tone.hex }}>
                    <CatIcon strokeWidth={1.8} style={{ width: 18, height: 18 }} />
                  </span>
                  <div className="text-[16px] font-semibold text-white leading-tight">{cat.label}</div>
                </div>

                <ul className="relative flex flex-col gap-1.5">
                  {cat.modules.map((m) => {
                    const ModIcon = MOD_ICONS[m.iconKey] || Sparkles
                    return (
                      <li key={m.label} className="flex items-center gap-2.5 rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2">
                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-white/10 bg-white/[0.03]"
                          style={{ color: tone.hex }}>
                          <ModIcon strokeWidth={1.8} style={{ width: 12, height: 12 }} />
                        </span>
                        <span className="text-[13px] font-semibold text-zinc-200">{m.label}</span>
                      </li>
                    )
                  })}
                </ul>
              </div>
            )
          })}
        </div>

        <div className="rounded-2xl border border-emerald-400/25 bg-emerald-400/[0.05] px-5 md:px-6 py-4 backdrop-blur-xl flex items-start gap-3">
          <Sparkles className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
          <p className="text-sm text-zinc-100 leading-relaxed">
            <span className="font-semibold text-white">Every licence includes the complete Kalki Ai Cloud ecosystem.</span>{' '}
            No feature is locked behind a higher tier. Higher tiers differentiate through service level.
          </p>
        </div>
      </Container>
    </section>
  )
}
